import type { ScoreBreakdown } from '@steelwake/shared/combat';
import { BALANCE } from '@steelwake/shared/data';
import { MODULES } from '@steelwake/shared/data';
import { MatchDetailSchema } from '@steelwake/shared/matchmaking';
import {
  REPORT_REASONS,
  ReportReasonSchema,
  ReportResultSchema,
  type ReportRequest,
} from '@steelwake/shared/progression';
import { Button, colors, fonts, radii, spacing } from '@steelwake/ui';
import { useEffect, useState } from 'react';

import { api, ApiError, describeError } from '../api.js';
import { useHudStore } from './store.js';

const TERMS: { key: keyof ScoreBreakdown; label: string; sign?: '-' }[] = [
  { key: 'damageDealt', label: 'Damage dealt × 1' },
  { key: 'roundWins', label: 'Round wins × 500' },
  { key: 'matchWin', label: 'Match win × 1500' },
  { key: 'accuracyBonus', label: 'Accuracy bonus (0–300)' },
  { key: 'timeBonus', label: 'Time bonus' },
  { key: 'firstBlood', label: 'First blood' },
  { key: 'selfDamage', label: 'Self-damage', sign: '-' },
];

/** How the match ended, in the player's terms. */
function outcomeLine(
  winner: number | null,
  reason: 'rounds' | 'forfeit',
  mySlot: number,
): { outcome: 'victory' | 'defeat' | 'draw'; title: string; sub: string | null } {
  if (winner === null) {
    return { outcome: 'draw', title: 'Draw', sub: 'Both pilots left the match; nobody is paid.' };
  }
  const won = winner === mySlot;
  if (reason === 'forfeit') {
    return won
      ? { outcome: 'victory', title: 'Victory', sub: 'The opponent forfeited the match.' }
      : { outcome: 'defeat', title: 'Defeat', sub: 'You forfeited the match.' };
  }
  return won
    ? { outcome: 'victory', title: 'Victory', sub: null }
    : { outcome: 'defeat', title: 'Defeat', sub: null };
}

const RECORD_POLL_MS = 1000;
const RECORD_WAIT_MS = 15_000;

type Recorded =
  | { state: 'recording' }
  | {
      state: 'recorded';
      credits: number;
      xp: number;
      rating: { before: number; after: number } | null;
      /** Names of the modules this seat played with (Phase 6). */
      modules: string[];
    }
  | { state: 'pending' };

/**
 * Asks the API for the settled match until it has one (or gives up after a while): what the
 * ledger actually paid this seat, never what the client computed.
 */
function useRecorded(matchId: string, mySlot: number): Recorded {
  const [recorded, setRecorded] = useState<Recorded>({ state: 'recording' });
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();
    const poll = async () => {
      try {
        const detail = MatchDetailSchema.parse(await api<unknown>(`/api/v1/match/${matchId}`));
        const mine = detail.players.find((p) => p.slot === mySlot);
        if (detail.settledAt !== null && mine) {
          if (!cancelled) {
            setRecorded({
              state: 'recorded',
              credits: mine.rewards?.credits ?? 0,
              xp: mine.rewards?.xp ?? 0,
              rating:
                mine.ratingBefore !== null && mine.ratingAfter !== null
                  ? { before: mine.ratingBefore, after: mine.ratingAfter }
                  : null,
              modules: (mine.loadout?.modules ?? []).map((id) => MODULES[id]?.name ?? id),
            });
          }
          return;
        }
      } catch {
        // Not recorded yet (the game server reports a moment after the end) or a passing
        // failure: ask again until the deadline.
      }
      if (cancelled) return;
      if (Date.now() - startedAt > RECORD_WAIT_MS) {
        setRecorded({ state: 'pending' });
        return;
      }
      timer = setTimeout(() => void poll(), RECORD_POLL_MS);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [matchId, mySlot]);
  return recorded;
}

function recordedText(r: Recorded): string {
  switch (r.state) {
    case 'recording':
      return 'Recording the result…';
    case 'pending':
      return 'Not recorded yet; your profile will show it shortly.';
    case 'recorded': {
      if (r.credits === 0 && r.xp === 0) return 'Recorded: nothing paid for this result.';
      const rating = r.rating
        ? `, rating ${String(Math.round(r.rating.before))} → ${String(Math.round(r.rating.after))}`
        : '';
      return `Recorded: +${String(r.credits)} credits, +${String(r.xp)} XP${rating}`;
    }
  }
}

/** What the server recorded and paid for this seat, once it has. */
function RecordedLine({ matchId, mySlot }: { matchId: string; mySlot: number }) {
  const recorded = useRecorded(matchId, mySlot);
  return (
    <div
      data-testid="results-recorded"
      data-state={recorded.state}
      data-credits={recorded.state === 'recorded' ? String(recorded.credits) : undefined}
      data-xp={recorded.state === 'recorded' ? String(recorded.xp) : undefined}
      style={{
        marginTop: spacing.md,
        fontSize: 14,
        fontWeight: 600,
        color: recorded.state === 'recorded' ? colors.text : colors.textMuted,
      }}
    >
      {recordedText(recorded)}
      {recorded.state === 'recorded' ? (
        <div data-testid="results-loadout" style={{ fontWeight: 400, color: colors.textMuted }}>
          Modules: {recorded.modules.length > 0 ? recorded.modules.join(', ') : 'none'}
        </div>
      ) : null}
    </div>
  );
}

const REASON_LABELS: Record<ReportRequest['reason'], string> = {
  cheating: 'Cheating',
  abuse: 'Abusive behaviour',
  other: 'Something else',
};

/** DECISIONS.md #37: a report on the opponent, filed against the match; Phase 7 reads the queue. */
function ReportOpponent({ matchId }: { matchId: string }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportRequest['reason']>('cheating');
  const [details, setDetails] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setState('sending');
    setError(null);
    try {
      ReportResultSchema.parse(
        await api<unknown>('/api/v1/report', {
          method: 'POST',
          body: { matchId, reason, details },
        }),
      );
      setState('done');
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'NO_OPPONENT'
          ? 'There is no pilot to report in a bot match.'
          : describeError(err),
      );
      setState('idle');
    }
  };
  if (state === 'done') {
    return (
      <p
        data-testid="report-done"
        role="status"
        style={{ fontSize: 12, color: colors.textMuted, margin: `${spacing.sm} 0 0` }}
      >
        Report sent. A moderator will look at the match.
      </p>
    );
  }
  if (!open) {
    return (
      <button
        type="button"
        data-testid="report-opponent"
        onClick={() => setOpen(true)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          marginTop: spacing.sm,
          color: colors.textMuted,
          textDecoration: 'underline',
          cursor: 'pointer',
          fontSize: 12,
          fontFamily: fonts.ui,
        }}
      >
        Report opponent
      </button>
    );
  }
  return (
    <div
      data-testid="report-form"
      style={{ display: 'grid', gap: spacing.sm, marginTop: spacing.sm, fontSize: 13 }}
    >
      <label style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
        <span style={{ color: colors.textMuted }}>Reason</span>
        <select
          data-testid="report-reason"
          value={reason}
          onChange={(e) => setReason(ReportReasonSchema.parse(e.target.value))}
        >
          {REPORT_REASONS.map((r) => (
            <option key={r} value={r}>
              {REASON_LABELS[r]}
            </option>
          ))}
        </select>
      </label>
      <textarea
        data-testid="report-details"
        value={details}
        maxLength={1000}
        rows={2}
        placeholder="What happened (optional)"
        onChange={(e) => setDetails(e.target.value)}
        style={{ fontFamily: fonts.ui, fontSize: 13 }}
      />
      {error ? (
        <span role="alert" data-testid="report-error" style={{ color: colors.danger }}>
          {error}
        </span>
      ) : null}
      <div style={{ display: 'flex', gap: spacing.sm }}>
        <Button
          data-testid="report-submit"
          onClick={() => void send()}
          disabled={state === 'sending'}
        >
          Send report
        </Button>
        <Button variant="ghost" data-testid="report-cancel" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const ONLINE_NOTE = `The server decided this result. A match decided by rounds pays when you dealt at least ${String(BALANCE.economy.minDamageForPayout)} damage, a forfeit's loser earns nothing, and credits stop at ${BALANCE.economy.dailyCreditCap.toLocaleString('en-US')} a day.`;

/**
 * The results overlay: every section 7 scoring term for both mechs; practice pays nothing.
 * `onRematch` is absent online, where a match cannot be replayed in place; `onQueue` is only
 * offered online.
 */
export function Results({
  onRematch,
  onQueue,
  onChangeMech,
}: {
  onRematch?: (() => void) | undefined;
  onQueue?: (() => void) | undefined;
  onChangeMech?: (() => void) | undefined;
}) {
  const snapshot = useHudStore((s) => s.snapshot);
  if (!snapshot?.result || !snapshot.myScore || !snapshot.enemyScore) return null;
  const { outcome, title, sub } = outcomeLine(
    snapshot.result.winner,
    snapshot.result.reason,
    snapshot.mySlot,
  );
  const won = outcome === 'victory';
  const online = snapshot.online;
  const mine = snapshot.myScore;
  const theirs = snapshot.enemyScore;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="results-title"
      data-testid="results"
      data-outcome={outcome}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(11, 13, 16, 0.6)',
        fontFamily: fonts.ui,
        zIndex: 7,
      }}
    >
      <div
        style={{
          width: 'min(640px, 94vw)',
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radii.md,
          padding: spacing.lg,
          color: colors.text,
        }}
      >
        <h1
          id="results-title"
          style={{
            margin: 0,
            fontSize: 26,
            letterSpacing: '0.16em',
            color: won ? '#7ed957' : outcome === 'draw' ? colors.textMuted : colors.danger,
          }}
        >
          {title}
        </h1>
        {sub ? (
          <div data-testid="results-reason" style={{ fontSize: 13, marginTop: 4 }}>
            {sub}
          </div>
        ) : null}
        <div style={{ color: colors.textMuted, fontSize: 13, marginBottom: spacing.md }}>
          {snapshot.me.name} {snapshot.wins[snapshot.mySlot]} ·{' '}
          {snapshot.wins[snapshot.mySlot === 0 ? 1 : 0]} {snapshot.enemy.name} · best of{' '}
          {String(snapshot.roundsToWin * 2 - 1)}
        </div>
        <table
          data-testid="results-table"
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
        >
          <thead>
            <tr style={{ color: colors.textMuted, textAlign: 'right' }}>
              <th style={{ textAlign: 'left', padding: '4px 0' }}>Term</th>
              <th style={{ color: snapshot.me.accent }}>{snapshot.me.name}</th>
              <th style={{ color: snapshot.enemy.accent }}>{snapshot.enemy.name}</th>
            </tr>
          </thead>
          <tbody>
            {TERMS.map((t) => (
              <tr key={t.key} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ padding: '4px 0' }}>{t.label}</td>
                <td style={{ textAlign: 'right' }} data-testid={`score-${t.key}`}>
                  {t.sign ?? ''}
                  {String(Math.round(mine[t.key]))}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {t.sign ?? ''}
                  {String(Math.round(theirs[t.key]))}
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: `2px solid ${colors.border}`, fontWeight: 700 }}>
              <td style={{ padding: '6px 0' }}>Match score</td>
              <td style={{ textAlign: 'right' }} data-testid="score-total">
                {String(mine.total)}
              </td>
              <td style={{ textAlign: 'right' }}>{String(theirs.total)}</td>
            </tr>
            <tr>
              <td>XP (score ÷ 5)</td>
              <td style={{ textAlign: 'right' }} data-testid="score-xp">
                {String(mine.xp)}
              </td>
              <td style={{ textAlign: 'right' }}>{String(theirs.xp)}</td>
            </tr>
            <tr>
              <td>Accuracy</td>
              <td style={{ textAlign: 'right' }}>{String(Math.round(mine.accuracy * 100))} %</td>
              <td style={{ textAlign: 'right' }}>{String(Math.round(theirs.accuracy * 100))} %</td>
            </tr>
            <tr>
              <td>Credits</td>
              <td style={{ textAlign: 'right' }} data-testid="score-credits">
                {String(mine.credits)}
              </td>
              <td style={{ textAlign: 'right' }}>{String(theirs.credits)}</td>
            </tr>
          </tbody>
        </table>
        {online ? <RecordedLine matchId={online.matchId} mySlot={snapshot.mySlot} /> : null}
        {online ? <ReportOpponent matchId={online.matchId} /> : null}
        <p
          style={{ color: colors.textMuted, fontSize: 12, margin: `${spacing.md} 0` }}
          data-testid="results-note"
        >
          {online
            ? ONLINE_NOTE
            : 'Practice pays nothing: these are the score and XP the match would award online. Rewards, unlocks and rating arrive with online play.'}
        </p>
        <div style={{ display: 'flex', gap: spacing.sm }}>
          {onRematch ? (
            <Button data-testid="rematch" onClick={onRematch}>
              Rematch
            </Button>
          ) : null}
          {onQueue ? (
            <Button data-testid="back-to-queue" onClick={onQueue}>
              Back to queue
            </Button>
          ) : null}
          {onChangeMech ? (
            <Button variant="ghost" data-testid="change-mech" onClick={onChangeMech}>
              Change mech
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
